import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import type { Moment } from 'moment';
import Notification from '@this/src/notification';
import type { HotelOrderItemTodoResponseArgs } from '@this/src/domain/arrangement/hotel_order_item_todo';
import HotelOrderItemTodo from '@this/src/domain/arrangement/hotel_order_item_todo';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import type { OrderItemJobTypeKey } from '@this/src/domain/order_item/order_item_job_type';
import { OrderItemJobTypeNumKeys } from '@this/src/domain/order_item/order_item_job_type';
import type { OrderItemStatusKey } from '@this/src/domain/order_item/order_item_status';
import { OrderItemStatusDone, OrderItemStatusOngoing } from '@this/src/domain/order_item/order_item_status';
import moment from 'moment';
import type { HotelElementProviderKey } from '@this/src/domain/hotel_element_provider';
import { HotelElementProviderAvailable } from '@this/src/domain/hotel_element_provider';
import useOrganizationSelector from '../../use_organization_selector';
import AllHotelsResultTable from './all_hotels_result_table';
import AllHotelsSearchArea, {
  type InventoryStatus,
  type AutoApprovedStatus,
  type SortKey,
  type SortDirection
} from './all_hotels_search_area';

interface Response {
  order_item_todos: HotelOrderItemTodoResponseArgs[];
  total_count: number;
  total_page: number;
}

const Items = observer(() => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [todos, setTodos] = useState<HotelOrderItemTodo[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPage, setTotalPage] = useState(1);
  const [page, setPage] = useState(parseInt(utils.getParam('page') || '1', 10));

  const [inventoryStatus, setInventoryStatus] = useState<InventoryStatus>(
    (utils.getParam('inventory_status') as InventoryStatus) || 'not_yet'
  );
  const [autoApprovedStatus, setAutoApprovedStatus] = useState<AutoApprovedStatus>(
    (utils.getParam('auto_approved_status') as AutoApprovedStatus) || 'auto'
  );
  const [jobTypes, setJobTypes] = useState<OrderItemJobTypeKey[]>(
    (utils.getParam('job_types')?.split(',').map(Number) as OrderItemJobTypeKey[]) || OrderItemJobTypeNumKeys
  );
  const [statuses, setStatuses] = useState<OrderItemStatusKey[]>(
    (utils.getParam('statuses')?.split(',').map(Number) as OrderItemStatusKey[]) ||
      OrderItemStatusOngoing.concat(OrderItemStatusDone)
  );
  const [providers, setProviders] = useState<HotelElementProviderKey[]>(
    (utils.getParam('providers')?.split(',') as HotelElementProviderKey[]) || HotelElementProviderAvailable
  );
  const [tripId, setTripId] = useState<number | undefined>(utils.getParamInt('trip_id') ?? undefined);
  const [startDateFrom, setStartDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('start_date_from') ?? moment()
  );
  const [startDateTo, setStartDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('start_date_to') ?? undefined
  );
  const [receiveDateFrom, setReceiveDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_from') ?? undefined
  );
  const [receiveDateTo, setReceiveDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_to') ?? undefined
  );
  const [organizationIds, renderOrganizationSelector] = useOrganizationSelector();
  const [logExists, setLogExists] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>((utils.getParam('sort_key') as SortKey) || 'started_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    (utils.getParam('sort_direction') as SortDirection) || 'asc'
  );
  const [showDescription, setShowDescription] = useState(false);
  const [isExternal, setIsExternal] = useState(false);
  const [isPaginate, setIsPaginate] = useState(true);
  const [isDiff, setIsDiff] = useState(false);
  const [isNotDiff, setIsNotDiff] = useState(true);
  const fetchingRef = useRef(false);

  const fetchTodos = useCallback(() => {
    if (fetchingRef.current) {
      fetchingRef.current = false;
      return;
    }
    setIsLoading(true);
    setTodos([]);
    setTotalCount(0);
    setTotalPage(1);

    const params = {
      page,
      inventory_status: inventoryStatus,
      auto_approved_status: autoApprovedStatus,
      job_types: jobTypes,
      statuses,
      providers,
      trip_id: tripId,
      start_date_from: startDateFrom?.format('YYYY-MM-DD'),
      start_date_to: startDateTo?.format('YYYY-MM-DD'),
      receive_date_from: receiveDateFrom?.format('YYYY-MM-DD'),
      receive_date_to: receiveDateTo?.format('YYYY-MM-DD'),
      organization_ids: organizationIds.join(','),
      log_exists: logExists,
      sort_key: sortKey,
      sort_direction: sortDirection,
      external: isExternal ? 'true' : 'false',
      pagination: isPaginate ? 'true' : 'false'
    };
    _.each(params, (value, key) => {
      if (value === undefined) {
        delete params[key as keyof typeof params];
      }
    });
    const query = _.map(params, (value, key) => `${key}=${value}`).join('&');
    history.push(`/arrangement/todo_list?tab=AUTO_RESERVATION_INVENTORY&sub_tab=ALL_HOTELS&${query}`);

    Fetcher.get<Response>('/arrangement/auto_reservation_inventory/all_hotels/items.json', params)
      .then(res => {
        setTodos(res.order_item_todos.map(args => new HotelOrderItemTodo(args)));
        setTotalCount(res.total_count);
        setTotalPage(res.total_page);
        if (res.total_page < page) {
          setPage(1);
        }
      })
      .catch(e => {
        Notification.error('データの取得に失敗しました');
      })
      .finally(() => setIsLoading(false));
  }, [
    page,
    inventoryStatus,
    autoApprovedStatus,
    jobTypes,
    statuses,
    providers,
    tripId,
    startDateFrom,
    startDateTo,
    receiveDateFrom,
    receiveDateTo,
    organizationIds,
    logExists,
    sortKey,
    sortDirection,
    isExternal,
    isPaginate,
    history
  ]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  return (
    <>
      <AllHotelsSearchArea
        isRakutenTab={false}
        jobTypes={jobTypes}
        statuses={statuses}
        autoApprovedStatus={autoApprovedStatus}
        inventoryStatus={inventoryStatus}
        providers={providers}
        tripId={tripId}
        startDateFrom={startDateFrom}
        startDateTo={startDateTo}
        receiveDateFrom={receiveDateFrom}
        receiveDateTo={receiveDateTo}
        showDescription={showDescription}
        logExists={logExists}
        sortKey={sortKey}
        sortDirection={sortDirection}
        isPaginate={isPaginate}
        isExternal={isExternal}
        isDiff={isDiff}
        isNotDiff={isNotDiff}
        setJobTypes={setJobTypes}
        setStatuses={setStatuses}
        setAutoApprovedStatus={setAutoApprovedStatus}
        setInventoryStatus={setInventoryStatus}
        setProviders={setProviders}
        setTripId={setTripId}
        setStartDateFrom={setStartDateFrom}
        setStartDateTo={setStartDateTo}
        setReceiveDateFrom={setReceiveDateFrom}
        setReceiveDateTo={setReceiveDateTo}
        setShowDescription={setShowDescription}
        setLogExists={setLogExists}
        setSortKey={setSortKey}
        setSortDirection={setSortDirection}
        setIsPaginate={setIsPaginate}
        setIsExternal={setIsExternal}
        setIsDiff={setIsDiff}
        setIsNotDiff={setIsNotDiff}
        renderOrganizationSelector={renderOrganizationSelector}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <AllHotelsResultTable
          todos={todos}
          totalCount={totalCount}
          totalPage={totalPage}
          page={page}
          showDescription={showDescription}
          fetchTodos={fetchTodos}
          setPage={setPage}
        />
      )}
    </>
  );
});

export default Items;
